import { useState } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useToastStore, ToastType } from '../stores/toastStore';

const iconMap: Record<ToastType, typeof CheckCircleIcon> = {
  success: CheckCircleIcon,
  error: XCircleIcon,
  warning: ExclamationTriangleIcon,
  info: InformationCircleIcon,
};

const classMap: Record<ToastType, string> = {
  success: 'toast-success',
  error: 'toast-error',
  warning: 'toast-warning',
  info: 'toast-info',
};

interface ToastItemProps {
  id: string;
  type: ToastType;
  message: string;
  onRemove: () => void;
}

function ToastItem({ id, type, message, onRemove }: ToastItemProps) {
  const [isLeaving, setIsLeaving] = useState(false);
  const Icon = iconMap[type];

  const handleRemove = () => {
    setIsLeaving(true);
    setTimeout(onRemove, 200);
  };

  return (
    <div
      className={`
        ${classMap[type]}
        ${isLeaving ? 'animate-slide-out' : ''}
      `}
    >
      <Icon className="w-5 h-5 flex-shrink-0" />
      <p className="text-sm font-medium flex-1">{message}</p>
      <button
        onClick={handleRemove}
        className="flex-shrink-0 p-1 rounded hover:bg-white/20 transition-colors"
      >
        <XMarkIcon className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function ToastContainer() {
  const { toasts, removeToast } = useToastStore();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          id={toast.id}
          type={toast.type}
          message={toast.message}
          onRemove={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
}

// Hook for easy access
export function useToast() {
  const { success, error, warning, info } = useToastStore();
  return { success, error, warning, info };
}
